import { useState, useEffect } from 'react';
import { Icons } from '../Icons';
import { Modal, ModalFooter } from '../Modal';
import type { Portfolio } from '../../services/portfolioManager';

interface PortfolioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; icon: string; color: string }) => void;
  editPortfolio?: Portfolio | null;
}

const AVAILABLE_ICONS = ['💼', '📊', '💰', '🚀', '💎', '📈', '🎯', '⭐', '🔥', '💵', '🏦', '📉', '💳', '🏆', '🎰'];

const AVAILABLE_COLORS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
];

/**
 * PortfolioModal Component
 * Modal pour créer ou éditer un portfolio
 */
export function PortfolioModal({ isOpen, onClose, onSave, editPortfolio }: PortfolioModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('💼');
  const [selectedColor, setSelectedColor] = useState('#6366f1');

  useEffect(() => {
    if (editPortfolio) {
      setName(editPortfolio.name);
      setDescription(editPortfolio.description || '');
      setSelectedIcon(editPortfolio.icon || '💼');
      setSelectedColor(editPortfolio.color || '#6366f1');
    } else {
      setName('');
      setDescription('');
      setSelectedIcon('💼');
      setSelectedColor('#6366f1');
    }
  }, [editPortfolio, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      icon: selectedIcon,
      color: selectedColor
    });

    onClose();
  };

  // V2.I.10 : moved from custom modal-overlay div to <Modal> (BaseModal).
  // Gains : focus trap, Escape close, restore focus, body scroll lock,
  // portal render, role=dialog/aria-modal=true.
  const footer = (
    <ModalFooter>
      <button type="button" onClick={onClose} className="btn-ghost">
        Annuler
      </button>
      <button
        type="submit"
        form="portfolio-modal-form"
        className="btn-primary"
        disabled={!name.trim()}
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <Icons.Save size={16} />
        {editPortfolio ? 'Sauvegarder' : 'Créer'}
      </button>
    </ModalFooter>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editPortfolio ? 'Éditer le portfolio' : 'Nouveau portfolio'}
      size="md"
      footer={footer}
    >
      <form id="portfolio-modal-form" onSubmit={handleSubmit}>
          {/* Nom */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Nom du portfolio *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Trading, HODL, DeFi..."
              required
              autoFocus
              style={{ width: '100%' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description du portfolio..."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          {/* Icône */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Icône
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
              gap: '8px'
            }}>
              {AVAILABLE_ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setSelectedIcon(icon)}
                  style={{
                    fontSize: '28px',
                    padding: '8px',
                    borderRadius: '8px',
                    border: selectedIcon === icon ? `2px solid ${selectedColor}` : '2px solid transparent',
                    backgroundColor: selectedIcon === icon ? 'var(--bg-secondary)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Couleur */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Couleur
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: '8px'
            }}>
              {AVAILABLE_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSelectedColor(color.value)}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    border: selectedColor === color.value ? `3px solid ${color.value}` : '3px solid transparent',
                    backgroundColor: color.value,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {color.name}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-secondary)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            borderLeft: `4px solid ${selectedColor}`
          }}>
            <span style={{ fontSize: '32px' }}>{selectedIcon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px' }}>
                {name || 'Nom du portfolio'}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
                {description || 'Description du portfolio'}
              </div>
            </div>
          </div>
      </form>
    </Modal>
  );
}
